import { Avatar, Box, Container, Divider, Grid, Paper, Stack, Typography } from "@mui/material"
import { DateTime } from "luxon"
import { ReactNode } from "react"
import { useSelector } from "react-redux"
import { profileSelector } from "reducers/profileSlice"

const emptyValue = "Chưa cập nhật"

const USER_POSITION_LABELS: Record<string, string> = {
  ACCOUNTANT: "Accountant",
  BA: "Business Analyst",
  BE: "Backend Engineer",
  BO: "Back Office",
  CO_FOUNDER: "Co-Founder",
  DESIGN: "Lead Designer",
  DEVOPS: "Devops Engineer",
  DIRECTOR: "Giám đốc",
  FE: "Frontend Engineer",
  FULL_STACK: "Fullstack Engineer",
  HR_ADMIN_MANAGER: "Trưởng phòng Nhân sự tổng hợp",
  HR_SPECIALIST: "Chuyên viên Nhân sự",
  INTERN: "Intern Engineer",
  MOBILE: "Mobile Engineer",
  OTHER: "Khác",
  PM: "Project Manager",
  QA: "Quality Assurance Engineer",
  SA: "Solution Architect/ Engineer",
}

const formatDate = (date?: null | string) => {
  if (!date) return emptyValue

  const parsed = DateTime.fromISO(date)
  return parsed.isValid ? parsed.toFormat("dd/MM/yyyy") : date
}

const formatPosition = (position?: null | string) => {
  if (!position) return emptyValue
  return USER_POSITION_LABELS[position] || position
}

const formatBoolean = (value?: boolean | null) => {
  if (value === undefined || value === null) return emptyValue
  return value ? "Có" : "Không"
}

const ProfileField = ({ label, value }: { label: string; value?: null | number | string }) => (
  <Box>
    <Typography color="text.secondary" variant="caption">
      {label}
    </Typography>
    <Typography className="break-words" fontWeight={500} variant="body2">
      {value || emptyValue}
    </Typography>
  </Box>
)

const ProfileSection = ({ children, title }: { children: ReactNode; title: string }) => (
  <Paper className="rounded-lg p-4" variant="outlined">
    <Stack gap={2}>
      <Typography fontWeight={700} variant="subtitle1">
        {title}
      </Typography>
      <Divider />
      <Grid container spacing={2}>
        {children}
      </Grid>
    </Stack>
  </Paper>
)

const Profile = () => {
  const { user } = useSelector(profileSelector)
  const attendee = user?.attendee
  const avatarText = (user?.fullName || user?.name || user?.username || "?").charAt(0).toUpperCase()
  const positionLabel = formatPosition(user?.position)

  return (
    <Container className="pb-6" maxWidth="lg">
      <Stack gap={3}>
        <Paper className="rounded-lg p-4" variant="outlined">
          <Stack alignItems={{ sm: "center", xs: "flex-start" }} direction={{ sm: "row", xs: "column" }} gap={2}>
            <Avatar
              src={attendee?.discordAvatarUrl || undefined}
              sx={{ bgcolor: "primary.main", fontSize: 32, height: 88, width: 88 }}
            >
              {avatarText}
            </Avatar>
            <Box minWidth={0}>
              <Typography className="break-words" fontWeight={700} variant="h5">
                {user?.fullName || user?.name || user?.username || emptyValue}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {user?.employeeCode || user?.username || emptyValue}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {positionLabel}
                {user?.department ? ` · ${user.department}` : ""}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <ProfileSection title="Thông tin cá nhân">
          <Grid item md={4} sm={6} xs={12}>
            <ProfileField label="Họ và tên" value={user?.fullName} />
          </Grid>
          <Grid item md={4} sm={6} xs={12}>
            <ProfileField label="Tên hiển thị" value={user?.name} />
          </Grid>
          <Grid item md={4} sm={6} xs={12}>
            <ProfileField label="Giới tính" value={user?.gender} />
          </Grid>
          <Grid item md={4} sm={6} xs={12}>
            <ProfileField label="Ngày sinh" value={formatDate(user?.dob)} />
          </Grid>
          <Grid item md={4} sm={6} xs={12}>
            <ProfileField label="Email công ty" value={user?.email} />
          </Grid>
          <Grid item md={4} sm={6} xs={12}>
            <ProfileField label="Email cá nhân" value={user?.personalEmail} />
          </Grid>
          <Grid item md={4} sm={6} xs={12}>
            <ProfileField label="Số điện thoại" value={user?.phoneNumber} />
          </Grid>
        </ProfileSection>

        <ProfileSection title="Thông tin công việc">
          <Grid item md={4} sm={6} xs={12}>
            <ProfileField label="Mã nhân viên" value={user?.employeeCode} />
          </Grid>
          <Grid item md={4} sm={6} xs={12}>
            <ProfileField label="Vị trí" value={positionLabel} />
          </Grid>
          <Grid item md={4} sm={6} xs={12}>
            <ProfileField label="Phòng ban / bộ phận" value={user?.department} />
          </Grid>
          <Grid item md={4} sm={6} xs={12}>
            <ProfileField label="Ngày gia nhập công ty" value={formatDate(user?.joinedCompanyAt)} />
          </Grid>
        </ProfileSection>

        <ProfileSection title="Thông tin Discord">
          <Grid item md={4} sm={6} xs={12}>
            <ProfileField label="Attendee ID" value={attendee?.id} />
          </Grid>
          <Grid item md={4} sm={6} xs={12}>
            <ProfileField label="Discord ID" value={attendee?.discordId} />
          </Grid>
          <Grid item md={4} sm={6} xs={12}>
            <ProfileField label="Discord name" value={attendee?.discordName} />
          </Grid>
          <Grid item md={4} sm={6} xs={12}>
            <ProfileField label="Tên attendee" value={attendee?.name} />
          </Grid>
          <Grid item md={4} sm={6} xs={12}>
            <ProfileField label="Part-time" value={formatBoolean(attendee?.isPartTime)} />
          </Grid>
        </ProfileSection>
      </Stack>
    </Container>
  )
}

export default Profile
